import type {
  IChatConversationDetail,
  IChatLocalMessage,
  IChatMember,
  IChatMessage,
} from "@/types/(my-space)/chat.interface";
import { audience } from "./ticks";

type PlayedMessage = Pick<IChatLocalMessage, "id" | "createdAt" | "sendState" | "playedBy">;

/** `playedBy` with `userId` in it — the same array when it already is (no re-render). */
export const mergePlayed = (playedBy: number[] | null | undefined, userId: number): number[] => {
  const list = playedBy ?? [];
  return list.includes(userId) ? list : [...list, userId];
};

/** `messages` with `userId` added to message `messageId`'s `playedBy`; same list if nothing changed. */
export const withPlayed = <M extends IChatMessage>(
  messages: M[],
  messageId: number,
  userId: number,
): M[] => {
  let changed = false;
  const next = messages.map((m) => {
    if (m.id !== messageId) return m;
    const playedBy = mergePlayed(m.playedBy, userId);
    if (playedBy === m.playedBy) return m;
    changed = true;
    return { ...m, playedBy };
  });
  return changed ? next : messages;
};

/**
 * One of MY voice notes has been played by everyone it was meant for — the
 * same audience the ticks count (everyone but me; outside a direct chat, not
 * who joined after it was sent). Never in a self chat, for an unsent note, or
 * when the API didn't give me the members (a broadcast's recipients).
 */
export const playedByAll = (
  message: PlayedMessage,
  members: Pick<IChatMember, "id" | "joinedAt">[] | null | undefined,
  myUserId: number | undefined,
  opts: { isDirect?: boolean; isSelf?: boolean } = {},
): boolean => {
  if (message.id <= 0 || message.sendState || opts.isSelf || !members?.length) return false;
  const others = audience(members, message, myUserId, !!opts.isDirect);
  if (!others.length) return false;
  const played = new Set(message.playedBy ?? []);
  return others.every((x) => played.has(x.id));
};

/** `playedByAll` from a conversation detail (none yet: not played). */
export const playedByAllIn = (
  chat: Pick<IChatConversationDetail, "type" | "isSelf" | "members"> | null | undefined,
  message: PlayedMessage,
  myUserId: number | undefined,
): boolean =>
  playedByAll(message, chat?.members, myUserId, {
    isDirect: chat?.type === "direct",
    isSelf: !!chat?.isSelf,
  });

/**
 * Someone else's stored voice note I haven't played yet (the dot by its
 * length). My own notes and pending ones never are.
 */
export const isUnplayedByMe = (
  message: Pick<IChatMessage, "id" | "type" | "isDeleted" | "sender" | "playedBy">,
  myUserId: number | undefined,
): boolean =>
  message.type === "voice" &&
  message.id > 0 &&
  !message.isDeleted &&
  myUserId != null &&
  message.sender?.id !== myUserId &&
  !(message.playedBy ?? []).includes(myUserId);
